import { useState } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import type { CompletionRollup } from '../components/completion-rollup';
import { Icon } from '../components/Icon';
import { ProgressBar } from '../components/ProgressBar';
import { UATSection } from '../components/UATSection';
import { DragHandle, SortableList, SortableNode } from '../dnd/Sortable';

export type UATSectionData = {
  id: string;
  title: string;
  /**
   * When set, replaces the auto-generated subtitle. Accepts ReactNode
   * so newly-created sections can render a "+ Add Subsections" link.
   */
  subtitle?: ReactNode;
  subsectionCount?: number;
  testCaseCount?: number;
  /** If true, renders an "Instructive" Pill next to the title. */
  instructive?: boolean;
  /**
   * F-013: interactive header slot forwarded to UATSection — rendered OUTSIDE
   * the toggle button (the Instructional-Section Decision control).
   */
  headerActions?: ReactNode;
  /**
   * Custom node rendered next to the title (e.g. an "N" new badge for
   * uat-16). Composes with `instructive`.
   */
  titleSuffix?: ReactNode;
  /**
   * Feature 025 — the Section's Completion Rollup, forwarded to the
   * `UATSection` header as the "Marked as Complete X/Y" indicator. Set by the
   * orchestrator only when the Section has available cases (omitted otherwise).
   */
  completion?: CompletionRollup;
  /** Optional rendered body shown when the section is expanded. */
  content?: ReactNode;
  /** Optional expanded column labels. Defaults to the three-column Study Management variant. */
  columns?: string[];
  /**
   * Optional content rendered immediately after this section in the
   * list — e.g. a NewSectionRow for the uat-15 "create section" flow.
   */
  afterRow?: ReactNode;
};

/** The Sort control's three-state cycle, mirrored from the web lens (F-022). */
export type UATSortOrder = 'manual' | 'testIdAsc' | 'testIdDesc';

export type UATPageProps = {
  title?: string;
  /**
   * Fires with the new title when the user renames it inline (tap the title →
   * edit → Enter/blur to commit). Optional — the title still edits locally for
   * presentational use when no handler is supplied.
   */
  onRenameTitle?: (next: string) => void;
  /** Stat line beneath the title. */
  stats?: string;
  /** Marked-as-complete count (numerator/denominator). */
  marked?: { current: number; total: number };
  sections: UATSectionData[];
  /**
   * Content rendered at the very top of the section list, before the first
   * section — e.g. an InlineNodeCreator for the "insert a Section at the top"
   * flow (the section-after-each-row bars live in `UATSectionData.afterRow`).
   */
  beforeRows?: ReactNode;
  /** Section ids that should start expanded (otherwise all collapsed). */
  initialOpenIds?: string[];
  /**
   * Fires with the resulting open-Section id set whenever a section is toggled
   * (F-024 capture seam). Lets a parent observe and persist the expanded tree
   * for navigation-state restore. Reports the user's real open set — `forceAllOpen`
   * never feeds it. Optional; other consumers are unaffected.
   */
  onOpenIdsChange?: (ids: readonly string[]) => void;
  searchPlaceholder?: string;
  /** Controlled search field value; leave unset for an uncontrolled field. */
  searchValue?: string;
  /** Fires with the full field value on every search keystroke. */
  onSearchChange?: (value: string) => void;
  /**
   * Force every section open (e.g. while a search filter is active). The
   * user's own toggle state is preserved underneath and restored when cleared.
   */
  forceAllOpen?: boolean;
  /**
   * Render each section as a sortable node with a drag handle (feature 015).
   * Requires an `AssemblyDnd` ancestor — the page itself stays presentational.
   */
  sectionsSortable?: boolean;
  /**
   * F-022 Assembly View Lens controls (controlled, like search). The page stays
   * presentational — it never sees WHY rows are missing, only reflects state and
   * forwards clicks. `hideActive` / `sortOrder` drive the active treatments
   * (US3); `onHideToggle` / `onSortCycle` fire one event per click (FR-013).
   */
  hideActive?: boolean;
  onHideToggle?: () => void;
  sortOrder?: UATSortOrder;
  onSortCycle?: () => void;
  /** Figma frame 1061:97842: dimmed first-load system recommendation coach mark. */
  systemRecommendationSpotlight?: boolean;
};

const pluralise = (count: number, singular: string): string =>
  `${count} ${count === 1 ? singular : `${singular}s`}`;

function buildSubtitle(s: UATSectionData): ReactNode {
  if (s.subtitle !== undefined) return s.subtitle;
  const parts: string[] = [];
  if (s.subsectionCount != null) parts.push(pluralise(s.subsectionCount, 'Subsection'));
  if (s.testCaseCount != null) parts.push(pluralise(s.testCaseCount, 'Test case'));
  return parts.join(' / ') || undefined;
}

const expandedSubitemSubtitle = (count: number): string => pluralise(count, 'Subitem');

function SystemRecommendationSpotlight() {
  return (
    <>
      <div
        data-uat-recommendation-scrim
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-40 bg-[#000212]/60"
      />
      <div
        data-uat-recommendation-spotlight
        className="pointer-events-none absolute left-[796px] top-[196px] z-50 h-[90px] w-[259px] rounded-md bg-surface-card p-[10px] shadow-lg"
      >
        <div className="grid h-full grid-cols-[1fr_48px] grid-rows-[30px_40px] gap-x-[7px] overflow-hidden rounded-sm bg-surface-card">
          <div className="col-start-2 flex items-center justify-center text-sm leading-[17px] text-text-primary">
            Action
          </div>
          <div className="row-start-2 flex h-10 items-center gap-2 rounded-sm bg-success-bg px-2 text-sm font-semibold leading-[17px] text-success-accent">
            <span className="min-w-0 flex-1 truncate">System recommendation</span>
            <Icon name="chevronRight" size={14} className="flex-shrink-0" />
          </div>
          <button
            type="button"
            aria-label="Include recommended test cases"
            tabIndex={-1}
            className="row-start-2 inline-flex h-[35px] w-12 self-center items-center justify-center rounded-sm border border-success-accent bg-surface-card text-success-accent"
          >
            <Icon name="plus" size={14} />
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * UAT (User Acceptance Testing) overview page — shown on the Assembly
 * step. Header summarises overall progress; a list of collapsible
 * sections lets the tester drill into subsections and individual test
 * cases. Caller supplies each section's expanded `content`.
 */
export function UATPage({
  title = 'User Acceptance Testing (UAT)',
  onRenameTitle,
  stats = '5 test cases identified • 3 require review • 2 medium confidence • 0 approved',
  marked = { current: 100, total: 150 },
  sections,
  beforeRows,
  initialOpenIds = [],
  onOpenIdsChange,
  searchPlaceholder = 'Search here',
  searchValue,
  onSearchChange,
  forceAllOpen = false,
  sectionsSortable = false,
  hideActive = false,
  onHideToggle,
  sortOrder = 'manual',
  onSortCycle,
  systemRecommendationSpotlight = false,
}: UATPageProps) {
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set(initialOpenIds));
  // Inline title rename (tap → edit → Enter/blur commits, Esc cancels). Kept in
  // local state so the rename is visible even without an `onRenameTitle` handler.
  const [name, setName] = useState(title);
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);
  const beginRename = () => {
    setDraftTitle(name);
    setEditingTitle(true);
  };
  const commitRename = () => {
    const trimmed = draftTitle.trim();
    if (trimmed && trimmed !== name) {
      setName(trimmed);
      onRenameTitle?.(trimmed);
    }
    setEditingTitle(false);
  };
  const pct = marked.total === 0 ? 0 : (marked.current / marked.total) * 100;
  // F-022/R7: the approved design has no active/open state for either control,
  // so we apply minimal token-based treatments (recorded liberty). Both buttons
  // shed their fixed widths so the Sort direction indicator fits.
  const sorting = sortOrder !== 'manual';
  const toolbarButton =
    'inline-flex cursor-pointer items-center justify-start gap-1 border-0 bg-transparent p-0 text-sm leading-[17px]';
  const toolbarTreatment = (active: boolean): string =>
    active ? 'font-semibold text-text-primary' : 'text-text-secondary hover:text-text-primary';

  const toggle = (id: string, next: boolean) => {
    const out = new Set(openIds);
    if (next) out.add(id);
    else out.delete(id);
    setOpenIds(out);
    // Write-through the user's real open set (event handler, not an effect — so
    // it never fires on mount and never emits the forceAllOpen view). F-024.
    onOpenIdsChange?.([...out]);
  };

  return (
    <div data-uat-page className="relative flex min-h-full flex-col bg-surface-app">
      <div className="border-b border-border bg-surface-card px-6 pb-3 pt-3">
        <div
          data-uat-header
          className="grid h-[59px] grid-cols-[1fr_auto] items-center gap-4"
        >
          <div className="flex flex-col gap-0">
            {editingTitle ? (
              <input
                autoFocus
                value={draftTitle}
                aria-label="Rename"
                onChange={(e) => setDraftTitle(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitRename();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setEditingTitle(false);
                  }
                }}
                className="m-0 w-full max-w-[560px] border-0 border-b border-[#515ffc] bg-transparent p-0 font-sans text-xl font-semibold leading-[1.4] tracking-[-0.4492px] text-[#292938] outline-none"
              />
            ) : (
              <h1 className="m-0 font-sans text-xl font-semibold leading-[1.4] tracking-[-0.4492px] text-[#292938]">
                <button
                  type="button"
                  onClick={beginRename}
                  title="Rename"
                  className="group inline-flex w-fit cursor-pointer items-center border-0 bg-transparent p-0 text-inherit transition-colors hover:text-[#515ffc]"
                >
                  {/* Edit affordance: a pencil revealed on hover, inheriting the
                      title colour (→ #515FFC on hover). */}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                    className="h-5 w-0 flex-shrink-0 overflow-visible opacity-0 transition-all duration-[350ms] ease-out group-hover:mr-2 group-hover:w-5 group-hover:opacity-100"
                  >
                    <path
                      d="M17.7594 5.73203L14.268 2.24141C14.1519 2.1253 14.0141 2.0332 13.8624 1.97037C13.7107 1.90753 13.5482 1.87519 13.384 1.87519C13.2198 1.87519 13.0572 1.90753 12.9056 1.97037C12.7539 2.0332 12.6161 2.1253 12.5 2.24141L2.86641 11.875C2.74983 11.9907 2.65741 12.1283 2.59451 12.28C2.5316 12.4317 2.49948 12.5944 2.50001 12.7586V16.25C2.50001 16.5815 2.6317 16.8995 2.86612 17.1339C3.10054 17.3683 3.41849 17.5 3.75001 17.5H16.875C17.0408 17.5 17.1997 17.4342 17.3169 17.3169C17.4342 17.1997 17.5 17.0408 17.5 16.875C17.5 16.7092 17.4342 16.5503 17.3169 16.4331C17.1997 16.3159 17.0408 16.25 16.875 16.25H9.00938L17.7594 7.5C17.8755 7.38392 17.9676 7.24611 18.0304 7.09444C18.0933 6.94276 18.1256 6.78019 18.1256 6.61602C18.1256 6.45184 18.0933 6.28927 18.0304 6.1376C17.9676 5.98592 17.8755 5.84811 17.7594 5.73203ZM7.24141 16.25H3.75001V12.7586L10.625 5.8836L14.1164 9.375L7.24141 16.25ZM15 8.49141L11.5094 5L13.3844 3.125L16.875 6.61641L15 8.49141Z"
                      fill="currentColor"
                    />
                  </svg>
                  {name}
                </button>
              </h1>
            )}
            <p className="text-sm font-normal leading-[1.4] tracking-[-0.1504px] text-[#67738e]">
              {stats}
            </p>
          </div>
          {/* Marked-as-Complete column — Figma: 310px wide, top label row 22px tall,
              6px progress bar 12px below, total block 40px tall. */}
          <div className="flex w-[310px] flex-col items-end gap-[12px]">
            <div className="flex w-full items-baseline justify-between">
              <span className="text-sm leading-[1.4] text-[#67738e]">Marked as Complete</span>
              <span className="text-xl font-normal leading-[1.4] tabular-nums tracking-[-0.4492px] text-[#292938]">
                {marked.current}/{marked.total}
              </span>
            </div>
            <div className="h-[6px] w-full">
              <ProgressBar
                value={pct}
                aria-label={`Marked as complete: ${marked.current} of ${marked.total}`}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        data-uat-workspace
        className="flex flex-1 flex-col bg-surface-workspace px-6 pb-6 pt-[15px]"
      >
        {/* Search + filter row. Search input is fixed 253px wide; Hide and Sort sit on
            the right edge as flat link-style buttons (no background). */}
        <div data-uat-toolbar className="-mx-[10px] flex items-center justify-between pb-2">
          <div className="w-[253px]">
            <span className="relative inline-flex h-[30px] w-full items-center rounded-md border border-border bg-surface-card">
              <span className="pointer-events-none absolute left-[6px] top-1/2 -translate-y-1/2 text-text-muted">
                <Icon name="search" size={16} />
              </span>
              <input
                aria-label="Search test cases"
                placeholder={searchPlaceholder}
                {...(searchValue !== undefined ? { value: searchValue } : {})}
                {...(onSearchChange
                  ? { onChange: (event) => onSearchChange(event.target.value) }
                  : {})}
                className="h-full w-full border-0 bg-transparent pl-[30px] pr-3 text-sm leading-[17px] text-text-primary outline-none placeholder:text-text-muted"
              />
            </span>
          </div>
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={onHideToggle}
              aria-pressed={hideActive}
              className={clsx(toolbarButton, toolbarTreatment(hideActive))}
            >
              <Icon name="eyeSlash" size={16} /> Hide
            </button>
            <button
              type="button"
              onClick={onSortCycle}
              aria-pressed={sorting}
              {...(sorting
                ? {
                    'aria-label': `Sort by Test ID, ${
                      sortOrder === 'testIdAsc' ? 'ascending' : 'descending'
                    }`,
                  }
                : {})}
              className={clsx(toolbarButton, toolbarTreatment(sorting))}
            >
              <Icon name="arrowsDownUp" size={16} /> Sort
              {sorting && (
                <Icon
                  name="chevronDown"
                  size={14}
                  className={clsx(sortOrder === 'testIdAsc' && 'rotate-180')}
                />
              )}
            </button>
          </div>
        </div>

        {/* Figma uat-01..04: list frame starts 8px below toolbar, with a 16px inner top inset. */}
        <div data-uat-section-list className="-mx-[10px] flex flex-col gap-[7px]">
          {beforeRows}
          {(() => {
            const items = sections.map((s, index) => {
              const isOpen = forceAllOpen || openIds.has(s.id);
              const isTerminalCollapsed = !isOpen && index === sections.length - 1;
              const subtitle =
                isOpen && s.subsectionCount != null
                  ? expandedSubitemSubtitle(s.subsectionCount)
                  : buildSubtitle(s);
              const card = (
                <UATSection
                  {...(s.headerActions === undefined ? {} : { headerActions: s.headerActions })}
                  {...(s.completion === undefined ? {} : { completion: s.completion })}
                  title={s.title}
                  subtitle={subtitle}
                  collapsedHeight={isTerminalCollapsed ? 'terminal' : 'default'}
                  titleSuffix={
                    <>
                      {s.instructive && (
                        <span className="inline-flex items-center gap-[3px] rounded-pill bg-[#515ffc]/10 py-[2px] pl-[2px] pr-[6px] text-sm font-normal leading-none text-[#515ffc]">
                          <Icon name="info" size={14} weight="fill" />
                          Instructive
                        </span>
                      )}
                      {s.titleSuffix}
                    </>
                  }
                  {...(s.content && {
                    columns: s.columns ?? ['Sub Section', 'Action', 'Mark as Completed'],
                  })}
                  open={isOpen}
                  onToggle={(next) => toggle(s.id, next)}
                >
                  {s.content}
                </UATSection>
              );

              if (!sectionsSortable) {
                return (
                  <div key={s.id} data-uat-section-item className="min-w-0">
                    {card}
                    {s.afterRow}
                  </div>
                );
              }

              // Sortable: the whole card is the pointer-drag target (no grip
              // icon) — the activation distance separates a click-to-toggle from
              // a drag. A visually-hidden handle keeps keyboard reordering. The
              // afterRow creator stays outside the drag wrapper. Works on
              // collapsed and open sections alike.
              return (
                <SortableNode key={s.id} id={s.id} kind="section" label={s.title}>
                  {({ handleProps, listeners, isDragging }) => (
                    // One block: the card and its trailing "New Section" creator
                    // are siblings in a single container (matching the
                    // non-sortable path) so paddings/spacings live in one place.
                    // The drag listeners sit on the block; the activation
                    // distance separates a click-to-toggle from a drag.
                    <div
                      data-uat-section-item
                      {...listeners}
                      className={clsx('relative min-w-0 touch-none', isDragging && 'opacity-50')}
                    >
                      <DragHandle label={s.title} handleProps={handleProps} srOnly />
                      {card}
                      {s.afterRow}
                    </div>
                  )}
                </SortableNode>
              );
            });
            return sectionsSortable ? (
              <SortableList ids={sections.map((s) => s.id)}>{items}</SortableList>
            ) : (
              items
            );
          })()}
        </div>
      </div>

      {systemRecommendationSpotlight && <SystemRecommendationSpotlight />}
    </div>
  );
}
